<template>
  <div class="px-4 py-4">

    <div class="row">
      <!-- Left Section: Products -->
      <div class="col-12 col-md-8 mb-4">

        <!-- Barcode Scanner Section -->
        <div class="mb-3 d-flex align-items-center">
          <div v-if="showBarcodeInput" class="flex-grow-1">
            <input
              ref="barcodeInput"
              type="text"
              class="form-control"
              :placeholder="$t('barcode')"
              v-model="barcodeInput"
              @keyup.enter="processBarcode"
              @blur="handleBarcodeBlur"
            />
          </div>
        </div>

        <!-- Categories and Products Section -->
        <div v-if="!showBarcodeInput" class="mb-4">
            <div class="input-group mb-3">
              <input
                type="text"
                class="form-control"
                :placeholder="$t('search')"
                v-model="search"
              />
              <button
                v-if="search && search.length > 1"
                class="btn btn-outline-secondary"
                type="button"
                @click="search = ''"
              >
                <AppIcon name="bi-x" class="bi bi-x" />
              </button>
            </div>
          <div class="mb-3" style="overflow-x: auto; overflow-y: hidden; white-space: nowrap; -webkit-overflow-scrolling: touch;">
            <div
              v-for="category in categories"
              :key="category.id"
              class="d-inline-block text-center me-2"
              style="cursor: pointer; width: 70px; height: 85px; vertical-align: top;"
              @click="toggleCategory(category.id)">
              <div
                :class="selected_categories.includes(category.id) ? 'border border-primary dashed-category  p-1' : 'border border-secondary  p-1'"
                style="width: 50px; height: 50px; overflow: hidden; margin: 0 auto;">
                <img
                  v-if="category.image"
                  :src="category.image"
                  alt="Category Image"
                  style="width: 100%; height: 100%; object-fit: cover;">
                <div
                  v-else
                  class="category-initial"
                  :style="{ backgroundColor: getCategoryColor(category) }"
                  aria-hidden="true">
                  {{ getCategoryInitial(category.name) }}
                </div>
              </div>
              <div
                :class="selected_categories.includes(category.id) ? 'text-primary' : 'text-secondary'"
                style="font-size: 10px; margin-top: 3px; height: 28px; line-height: 1.2; overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; line-clamp: 2; -webkit-box-orient: vertical; word-break: break-word;">
                {{ category.name }}
              </div>
            </div>
          </div>
          <div class="position-relative">
            <div class="card-body p-1 p-md-2" id="pos-products" style="max-height: 75vh!important; min-height: 50vh; overflow-y: auto; overflow-x: hidden;">
              <div class="row row-cols-2 row-cols-sm-3 row-cols-md-4 row-cols-lg-5 g-2 g-md-3">

                <div v-for="(product,index) in products" :key="index" class="col">
                  <div class="card h-100 shadow-sm position-relative" @click="addToCart(product)">
                    <!-- Price Badge in Corner -->
                    <span class="badge bg-primary position-absolute top-0 end-0 m-1" style="font-size: 10px;z-index:120">
                      {{ formatMoney(product.inventories[0].price) }}
                    </span>
                    <div class="card-img-wrapper">
                      <div
                        v-if="!product.image"
                        class="card-img-top product-initials"
                        :style="{ backgroundColor: getProductColor(product) }"
                        aria-hidden="true">
                        {{ getProductInitials(product.label) }}
                      </div>
                      <img v-else :src="product.image" class="card-img-top" :alt="product.label">
                    </div>
                    <div class="card-body text-center p-1">
                      <h5 class="card-title mb-0" style="font-size: 11px; font-weight: 500; line-height: 1.2;">{{ product.label }}</h5>
                      <p class="card-text mb-0" style="font-size: 10px;">Qty: {{ product.inventories[0].quantity }}</p>
                    </div>
                  </div>
                </div>

              </div>
                <table-loading-bar v-if="loading" class="page-table-state text-center" />
                <div v-if="!loading && !products.length && !isFiltered && fetched_all_products" class="text-center py-5 my-5">
                  <AppIcon name="bi-box-seam" class="bi bi-box-seam text-muted" style="font-size: 3rem;" />
                  <h4 class="mt-3">{{ $t('no_products_in_pos') }}</h4>
                  <p class="text-muted">{{ $t('add_products_to_start_selling') }}</p>
                  <button class="btn btn-outline-primary mt-2" @click="$router.push('/products')">
                    <AppIcon name="bi-plus-circle" class="bi bi-plus-circle me-2" />{{ $t('add_products') }}
                  </button>
                </div>
                <div v-if="!loading && !products.length && isFiltered" class="text-center py-5 my-5 w-100">
                  <AppIcon name="bi-search" class="bi bi-search text-muted" style="font-size: 3rem;" />
                  <h4 class="mt-3">{{ $t('no_product_found') }}</h4>
                  <p class="text-muted">{{ $t('try_different_search_term') || 'Try a different search term or category.' }}</p>
                  <button class="btn btn-outline-secondary mt-2" @click="clearProductFilters">
                    <AppIcon name="bi-x-circle" class="bi bi-x-circle me-2" />{{ $t('clear_search') || 'Clear Search' }}
                  </button>
                </div>

            </div>
          </div>
        </div>
      </div>

      <!-- Mobile Cart Button - Only visible on small devices -->
      <div class="d-md-none fixed-bottom mb-3">
        <div class="d-flex justify-content-between">
          <button
            @click="showReceiptModal = true"
            class="btn btn-primary py-2 rounded-pill shadow-lg w-100 position-relative"
          >
            <AppIcon name="bi-cart-fill" class="bi bi-cart-fill me-2" /> {{ $t('view_cart') }}
            <span v-if="cart.length" class="badge bg-danger position-absolute top-0 start-100 translate-middle rounded-pill" style="left:95% !important">
              {{ cart.length }}
            </span>
            <div class="ms-2">{{ formatMoney(cartTotal) }}</div>
          </button>
        </div>
      </div>

      <!-- Right Section: Receipt - Hidden on mobile, visible on md and up -->
      <div class="col-md-4 d-none d-md-block">
        <div class="x-receipt">
            <div class="d-flex justify-content-between mb-2 flex-wrap gap-1">
              <!-- Kiosk Mode Toggle: hides navbar/sidebar and toggles fullscreen -->
              <button
                 class="btn btn-secondary btn-sm flex-fill"
                 type="button"
                 @click="toggleKioskMode"
                 :title="kioskMode ? $t('exit_fullscreen') : $t('fullscreen')"
               >
                 <AppIcon name="bi-fullscreen" v-if="!kioskMode" class="bi bi-fullscreen" style="font-size:18px" />
                 <AppIcon name="bi-fullscreen-exit" v-else class="bi bi-fullscreen-exit" style="font-size:18px" />
               </button>
               <button
                  class="btn btn-secondary btn-sm flex-fill"
                  type="button"
                  @click="toggleBarcodeInput"
                  :title="$t('barcode_scanner')"
                >
                  <AppIcon name="bi-upc-scan" class="bi bi-upc-scan" style="font-size:18px" />
                </button>

              <button
                  class="btn btn-secondary btn-sm flex-fill"
                  type="button"
                  @click="showRegisterModal"
                  :title="$t('cash_register')"
                >
                  <AppIcon name="bi-cash-coin" class="bi bi-cash-coin" style="font-size:18px" />
                </button>

              <button class="btn btn-secondary btn-sm flex-fill" :disabled="!this.cart.length" @click="openCreateClientModal" :title="$t('client')">
              <span class="d-inline-block .text-primary-dark">
                <AppIcon name="bi-people-fill" class="bi bi-people-fill" style="font-size:18px" />
              </span>
              </button>
              <button class="btn btn-secondary btn-sm flex-fill" :disabled="!this.cart.length" @click="openDiscountModal" :title="$t('discount')">
              <span class="d-inline-block .text-primary-dark">
                <AppIcon name="bi-percent" class="bi bi-percent" style="font-size:18px" />
              </span>
              </button>
              <button class="btn btn-secondary btn-sm flex-fill" :disabled="!this.cart.length" @click="openNotesModal" :title="$t('notes')">
                <span class="d-inline-block .text-primary-dark">
                  <AppIcon name="bi-file-earmark-text-fill" class="bi bi-file-earmark-text-fill" style="font-size:18px" />
                </span>
              </button>



              <button class="btn btn-success btn-sm flex-fill" @click="cancelCart" :title="$t('reset')">
                <span class="d-inline-block .text-primary-dark">
                  <AppIcon name="bi-arrow-clockwise" class="bi bi-arrow-clockwise" style="font-size:18px" />
                </span>
              </button>
            </div>
          <!-- Removed Cashier and Payment Method Selects -->
          <div class="card shadow-sm">
          <div class="card-body p-0">
              <!-- Scrollable Cart Items -->
              <div class="scrollable-cart p-2">
                <div v-if="cart.length > 0">
                  <ul class="list-group list-group-flush" style="max-height: 55vh; overflow-y: auto;padding:0 !important;font-size: 13px;">
                    <li v-for="(item, index) in cart" :key="index" class="list-group-item" @click="toggleControls(index)" style="padding: 0 !important">
                      <div class="d-flex justify-content-between align-items-start mb-1">
                        <!-- Product name with unit price below -->
                        <div>
                          <span class="fw-medium d-block">{{ item.label }} <span class="text-muted small">× {{ item.quantity }}</span></span>
                          <!-- <span class="text-muted small d-block">{{ formatMoney(item.inventories[0].price) }} × {{ item.quantity }}</span> -->
                        </div>
                        <!-- Total price -->
                        <div class="text-end">
                          <span class="fw-bold">{{ formatMoney(item.totalPrice) }}</span>
                        </div>
                      </div>

                      <!-- Quantity and remove controls -->
                      <div
                        v-if="selectedItemIndex === index"
                        class="d-flex justify-content-between align-items-center mt-1"
                      >
                        <div class="card-settings-btn-qty">
                          <button class="btn btn-secondary btn-sm" @click.stop="decreaseQuantity(item)">-</button>
                          <span class="btn btn-outline-secondary btn-sm disabled">{{ item.quantity }}</span>
                          <button class="btn btn-secondary btn-sm" @click.stop="increaseQuantity(item)">+</button>
                          <button class="btn btn-danger btn-sm ms-2" @click.stop="removeFromCart(index)">x</button>
                        </div>
                        <div class="mt-2" @click.stop>
                          <input
                            type="number"
                            class="form-control form-control-sm"
                            placeholder="Update Price"
                            v-model.number="item.inventories[0].price"
                            @input="updatePrice(item)"
                          />
                        </div>
                      </div>
                    </li>
                  </ul>
                </div>
                <div v-else class="text-center text-muted py-4">
                  <p class="mb-0">{{$t('no_product_found')}}</p>
                  <p class="small">{{$t('click_to_add')}}</p>
                </div>
              </div>
              <!-- Fixed Bottom Section -->
              <div class="fixed-bottom-section">

                <div v-if="client" class="d-flex justify-content-between mt-1">
                  <span><b>{{$t('client')}}:</b></span>
                  <span>{{ client.name }}</span>
                </div>

                <div v-if="notes" class="d-flex justify-content-between mt-1">
                  <span><b>{{$t('notes')}}:</b></span>
                  <span>{{ notes }}</span>
                </div>
                <div>
                  <div class="d-flex justify-content-between mb-1">
                    <span><b>{{$t('sub_total')}}: </b></span>
                    <span>{{formatMoney(subTotal)}} </span>
                  </div>
                  <div v-if="discount > 0" class="d-flex justify-content-between mb-1">
                    <span><b>{{$t('discount')}}: </b><small>{{ discount }}%</small></span>
                    <span>-{{formatMoney(totalDiscount)}} </span>
                  </div>
                  <div v-if="cart.length" class="d-flex justify-content-between mb-1">
                    <span><b>{{$t('vat')}}:</b> <small>{{ business.vat }}% </small></span>
                    <span>{{formatMoney(totalTax)}}</span>
                  </div>
                  <div class="d-flex justify-content-between mb-2 fw-bold">
                    <span>{{$t('total')}}:</span>
                    <span>{{ formatMoney(cartTotal) }}</span>
                  </div>
                </div>

                <div class="d-flex justify-content-between mt-3" v-if="helper.checkAccess('create_pos') != -1">
                  <button @click="openChargeModal" v-if="cart.length && submittedCart.length == 0" class="btn btn-success w-100 pos-charge-button" :disabled="!this.cart.length || loading === true || submittedCart.length > 0">
                      <span class="d-inline-block me-2 .text-primary-dark">
                        <AppIcon name="bi-credit-card-fill" class="bi bi-credit-card-fill pos-charge-icon" />
                      </span>
                    {{$t('charge') || 'Charge'}}
                  </button>
                </div>
                <button @click="printReceipt"  v-if="previousCartStatus == 'paid' && submittedCart.length > 0" class="btn btn-secondary mt-2 w-100">
                   <span class="d-inline-block me-2 .text-primary-dark">
                      <AppIcon name="bi-printer-fill" class="bi bi-printer-fill" />
                      </span>
                    {{$t('print_receipt') || 'Print Receipt'}}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>

    <!-- Receipt Modal for Mobile -->
    <div class="modal fade" :class="{ 'show d-block': showReceiptModal }" tabindex="-1" role="dialog">
      <div class="modal-dialog modal-dialog-scrollable modal-fullscreen-md-down">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ $t('view_cart') }}</h5>
            <button type="button" class="btn-close" @click="showReceiptModal = false"></button>
          </div>
          <div class="modal-body pb-0">
            <!-- Action Buttons -->
            <div class="d-flex justify-content-between mb-3">
              <button class="btn btn-secondary btn-sm flex-grow-1 me-1" :disabled="!this.cart.length" @click="openCreateClientModal">
                <AppIcon name="bi-people-fill" class="bi bi-people-fill" />
              </button>
              <button class="btn btn-secondary btn-sm flex-grow-1 me-1" :disabled="!this.cart.length" @click="openDiscountModal">
                <AppIcon name="bi-percent" class="bi bi-percent" />
              </button>
              <button class="btn btn-secondary btn-sm flex-grow-1 me-1" :disabled="!this.cart.length" @click="openNotesModal">
                <AppIcon name="bi-file-earmark-text-fill" class="bi bi-file-earmark-text-fill" />
              </button>
              <button class="btn btn-secondary btn-sm flex-grow-1 me-1" @click="cancelCart">
                <AppIcon name="bi-x-circle-fill" class="bi bi-x-circle-fill" />
              </button>

            </div>

            <!-- Cart Items -->
            <div v-if="cart.length > 0">
              <ul class="list-group list-group-flush">
                <li v-for="(item, index) in cart" :key="index" class="list-group-item" @click="toggleControls(index)">
                  <div class="d-flex justify-content-between align-items-start mb-1">
                    <div>
                      <span class="fw-medium d-block">{{ item.label }}</span>
                      <span class="text-muted small d-block">{{ formatMoney(item.inventories[0].price) }} × {{ item.quantity }}</span>
                    </div>
                    <div class="text-end">
                      <span class="fw-bold">{{ formatMoney(item.totalPrice) }}</span>
                    </div>
                  </div>

                  <!-- Quantity and remove controls -->
                  <div
                    v-if="selectedItemIndex === index"
                    class="d-flex justify-content-between align-items-center mt-1"
                  >
                    <div>
                      <button class="btn btn-secondary btn-sm" @click.stop="decreaseQuantity(item)">-</button>
                      <span class="btn btn-outline-secondary btn-sm disabled">{{ item.quantity }}</span>
                      <button class="btn btn-secondary btn-sm" @click.stop="increaseQuantity(item)">+</button>
                      <button class="btn btn-danger btn-sm ms-2" @click.stop="removeFromCart(index)">x</button>
                    </div>
                    <div class="mt-2" @click.stop>
                      <input
                        type="number"
                        class="form-control form-control-sm"
                        placeholder="Update Price"
                        v-model.number="item.inventories[0].price"
                        @input="updatePrice(item)"
                      />
                    </div>
                  </div>
                </li>
              </ul>
            </div>
            <div v-else class="text-center text-muted py-4">
              <p class="mb-0">{{$t('no_product_found')}}</p>
              <p class="small">{{$t('click_to_add')}}</p>
            </div>
          </div>
          <!-- Cart Summary & Checkout Buttons -->
          <div class="modal-footer flex-column">
            <!-- Cart summary -->
            <div class="w-100">
              <div v-if="client" class="d-flex justify-content-between mb-1">
                <span><b>{{$t('client')}}:</b></span>
                <span>{{ client.name }}</span>
              </div>
              <div v-if="notes" class="d-flex justify-content-between mb-1">
                <span><b>{{$t('notes')}}:</b></span>
                <span>{{ notes }}</span>
              </div>
              <div>
                  <div class="d-flex justify-content-between mb-1">
                    <span><b>{{$t('sub_total')}}: </b></span>
                    <span>{{formatMoney(subTotal)}} </span>
                  </div>
                  <div v-if="discount > 0" class="d-flex justify-content-between mb-1">
                    <span><b>{{$t('discount')}}: </b><small>{{ discount }}%</small></span>
                    <span>-{{formatMoney(totalDiscount)}} </span>
                  </div>
                  <div v-if="cart.length" class="d-flex justify-content-between mb-1">
                    <span><b>{{$t('vat')}}:</b> <small>{{ business.vat }}% </small></span>
                    <span>{{formatMoney(totalTax)}}</span>
                  </div>
                  <div class="d-flex justify-content-between mb-2 fw-bold">
                    <span>{{$t('total')}}:</span>
                    <span>{{ formatMoney(cartTotal) }}</span>
                  </div>
              </div>

            </div>

            <div class="d-flex w-100" v-if="helper.checkAccess('create_pos') != -1">
              <button @click="openChargeModal" class="btn btn-success w-100 pos-charge-button" :disabled="!this.cart.length || loading === true">
                <AppIcon name="bi-credit-card-fill" class="bi bi-credit-card-fill pos-charge-icon me-2" />
                {{$t('charge') || 'Charge'}}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Modal Overlay -->
    <div class="modal-backdrop fade show" v-if="showReceiptModal" @click="showReceiptModal = false"></div>

    <!-- client modal -->
    <search-or-add-client
      v-if="createClientState"
      :state="createClientState"
      @saveClient="saveClient"/>
    <!-- client modal -->

    <notes-modal
      v-if="notesModalState"
      :state="notesModalState"
      @close="notesModalState = false"
      :notes="notes"
      @saveNotes="saveNotes"
    />

    <discount-modal
      v-if="discountModalState"
      :state="discountModalState"
      :discount="discount"
      @close="discountModalState = false"
      @saveDiscount="saveDiscount"
    />

    <transactions-modal
      v-if="transactionsModalState"
      :state="transactionsModalState"
      @close="transactionsModalState = false"
      @continue-transaction="loadTransactionToPos"
    />

    <checkout-result-modal
      v-if="checkoutResultModalState"
      :state="checkoutResultModalState"
      :details="checkoutDetails"
      :transactionId="lastTransactionId"
      :paymentMethod="paymentMethod"
      :notes="notes"
      :client="client"
      :business="business"
      @close="checkoutResultModalState = false"
      @print="printReceipt"
      @new-order="cancelCart"
      @transaction-updated="handleTransactionUpdated"
      @continue-transaction="loadTransactionToPos"
    />

    <checkout-result-modal
      v-if="checkoutResultModalState"
      :state="checkoutResultModalState"
      :details="checkoutDetails"
      :transactionId="lastTransactionId"
      :paymentMethod="paymentMethod"
      :notes="notes"
      :client="client"
      :business="business"
      @close="checkoutResultModalState = false"
      @print="printReceipt"
      @new-order="cancelCart"
      @transaction-updated="handleTransactionUpdated"
      @continue-transaction="loadTransactionToPos"
    />

    <!-- Variant Selection Modal -->
    <CustomModal
      :isOpen="showVariantModal"
      @modalClosed="showVariantModal = false"
      :modalId="'variant-modal'"
      :showFooter="false"
    >
      <template #header>
        <h5 class="modal-title">{{ $t('select_variant') }}</h5>
        <button type="button" class="btn-close" @click="showVariantModal = false"></button>
      </template>
      <template #body>
        <div class="list-group" v-if="selectedProductForVariants">
          <button
            v-for="variant in selectedProductForVariants.variants"
            :key="variant.id"
            type="button"
            class="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
            @click="addVariantToCart(variant)"
          >
            <div>
                <div class="fw-bold">{{ variant.name }}</div>
                <small class="text-muted">Qty: {{ variant.inventory ? variant.inventory.quantity : 0 }}</small>
            </div>
            <span class="badge bg-primary rounded-pill">{{ formatMoney(variant.price) }}</span>
          </button>
        </div>
      </template>
    </CustomModal>

    <!-- Charge Modal -->
    <ChargeModal
      v-if="chargeModalState"
      :state="chargeModalState"
      :totalAmount="cartTotal"
      :initialNotes="notes"
      :receiptHtml="currentReceiptHtml"
      :isProcessing="loading"
      :completedTransactionId="lastTransactionId"
      :transactionId="checkoutDetails && checkoutDetails.id"
      :paymentMethods="activePaymentMethods"
      @close="handleChargeModalClose"
      @confirm="handleChargeConfirm"
      @notes-updated="notes = $event"
      @print="printReceipt"
    />

    <!-- Cash Register Modal -->
    <CashRegisterModal
      :isModalOpen="isModalOpen"
      @modalClosed="isModalOpen = false"
      @registerStatusChanged="fetchCurrentRegister"
    />

  </div>
</template>

<script>
import helper from '../../helper';
import NotesModal from '../../components/NotesModal.vue';
import DiscountModal from '../../components/DiscountModal.vue';
import SearchOrAddClient from '../../components/SearchOrAddClient.vue';

import CheckoutResultModal from '../../components/CheckoutResultModal.vue';
import CustomModal from '../../components/CustomModal.vue';
import ChargeModal from '../../components/ChargeModal.vue';
import { getDefaultPaymentMethods } from '../../constants/paymentMethods';

export default {
  name: 'Pos',
  persistedFilterState: { key: 'pos-products', fields: ['search', 'selected_categories'] },
  components: {
    NotesModal,
    DiscountModal,
    SearchOrAddClient,

    CheckoutResultModal,
    CustomModal,
    ChargeModal,
    CashRegisterModal: () => import('./PosCashRegister.vue')
  },
  data() {
    return {
      loading : false,
      createClientState : false,
      cart: [],
      submittedCart : [],
      previousCartStatus : null,
      products : [],
      selectedItemIndex: null,
      registerStatus : 'closed',
      createOrderModalState : false,
      isModalOpen : false,
      currentRegister : {},
      search: '',
      timer_id: null,
      page : 1,
      per_page : 25,
      fetched_all_products: false,
      client : null,
      notesModalState: false,
      notes: '',
      discountModalState: false,
      discount: 0,
      paymentMethod: '',
      paymentMethods: [],
      loadingPaymentMethods: false,
      transactionsModalState: false,
      showReceiptModal: false,
      users : [],
      categories : [
        {
          id : 1,
          name : "all"
        },
        {
          id : 2,
          name : "electronics"
        }
      ],
      selected_categories : [],
      showBarcodeInput: false,
      barcodeInput: '',
      lastTransactionId: null,
      editingTransactionId: null, // Track transaction being edited
      printerIntegration: { enabled: false, settings: { url: '' } },
      checkoutResultModalState: false,
      checkoutDetails: null,
      kioskMode: false,
      prevNavFullSize: null,
      showVariantModal: false,
      selectedProductForVariants: null,
      chargeModalState: false,
      currentReceiptHtml: null
    };
  },
  computed: {
    // Subtotal before tax, AFTER discount (net base). Displayed as "sub_total"
    totalWithoutDiscount() {
      const netBeforeDiscount = this.subTotal;
      const netAfterDiscount = this.discount > 0 ? netBeforeDiscount * (1 - this.discount / 100) : netBeforeDiscount;
      return netAfterDiscount;
    },
    subTotal() {
      const vat = Number(this.business?.vat || 0);
      const globalPriceIncludesTax = this.business?.price_includes_tax === true;
      return this.cart.reduce((sum, item) => {
        const itemPrice = Number(item.inventories[0].price) || 0;
        const itemQty = Number(item.quantity) || 0;
        const itemGross = itemPrice * itemQty;
        const itemPriceIncludesTax = (item.price_includes_tax === true) || (item.product?.price_includes_tax === true) || globalPriceIncludesTax;
        const itemNet = (itemPriceIncludesTax && vat > 0) ? itemGross * (100 / (100 + vat)) : itemGross;
        return sum + itemNet;
      }, 0);
    },
    // Currency discount amount applied on the net base
    totalDiscount() {
      const netBeforeDiscount = this.subTotal;
      return netBeforeDiscount * (this.discount / 100);
    },
    // VAT computed on the discounted net base
    totalTax() {
      const vat = Number(this.business?.vat || 0);
      const netAfterDiscount = this.totalWithoutDiscount; // already net and discounted
      return netAfterDiscount * (vat / 100);
    },
    // Grand total: net after discount + VAT. Matches both price models
    cartSubtotal() {
      return Number((this.totalWithoutDiscount + this.totalTax).toFixed(2));
    },
    cartTotal() { return this.cartSubtotal; },
    isFiltered() {
      return (this.search && this.search.length > 0) ||
             (this.selected_categories && this.selected_categories.length > 0);
    },
    helper(){ return helper},
    business(){ return this.$store.getters.BUSINESS },
    user(){ return this.$store.getters.USER },
    activePaymentMethods(){
        // Filter only active payment methods or return the standard defaults
        const activeMethods = this.paymentMethods.filter(method => method.is_active)

        // Keep payment options available if the API cannot provide them
        if (activeMethods.length === 0) {
            return getDefaultPaymentMethods()
        }

        return activeMethods
    }
  },
  methods: {
    clearProductFilters() {
      clearTimeout(this.timer_id)
      this.timer_id = null
      this.search = ''
      this.selected_categories = []
      this.page = 1
      this.products = []
      this.fetched_all_products = false
      this.getProducts()
    },
    getCategoryInitial(name) {
      const normalizedName = String(name || '').trim()
      return normalizedName ? Array.from(normalizedName)[0].toLocaleUpperCase() : '?'
    },
    getCategoryColor(category) {
      const colors = [
        '#E11D48', '#EA580C', '#CA8A04', '#16A34A',
        '#0D9488', '#0284C7', '#2563EB', '#7C3AED',
        '#C026D3', '#DB2777', '#475569', '#4F46E5'
      ]
      const colorKey = String(category.id || category.name || '')
      const hash = Array.from(colorKey).reduce(
        (value, character) => ((value * 31) + character.codePointAt(0)) >>> 0,
        0
      )

      return colors[hash % colors.length]
    },
    getProductInitials(label) {
      const words = String(label || '').trim().split(/\s+/).filter(Boolean)
      if (!words.length) return '?'

      const initials = words.length === 1
        ? Array.from(words[0]).slice(0, 2)
        : [Array.from(words[0])[0], Array.from(words[1])[0]]

      return initials.join('').toLocaleUpperCase()
    },
    getProductColor(product) {
      const colors = [
        '#E11D48', '#EA580C', '#CA8A04', '#16A34A',
        '#0D9488', '#0284C7', '#2563EB', '#7C3AED',
        '#C026D3', '#DB2777', '#475569', '#4F46E5'
      ]
      const colorKey = String(product.id || product.label || '')
      const hash = Array.from(colorKey).reduce(
        (value, character) => ((value * 31) + character.codePointAt(0)) >>> 0,
        0
      )

      return colors[hash % colors.length]
    },
    async fetchPaymentMethods(forceDefault = false) {
      this.loadingPaymentMethods = true
      try {
        const response = await helper.customGet(`${process.env.VUE_APP_API}/payment-methods?per_page=100`)
        this.paymentMethods = response.data.data || []

        // If no payment methods exist, use the standard defaults
        if (this.paymentMethods.length === 0) {
          this.paymentMethods = getDefaultPaymentMethods()
        }

        // Wait for loading to complete before setting default
        this.loadingPaymentMethods = false

        // Set the default payment method if not already defined (check for empty string too) or if forced
        if (forceDefault || !this.paymentMethod || this.paymentMethod === '') {
          await this.$nextTick()

          const defaultMethod = this.paymentMethods.find(method => method.is_default === true)
          let defaultSlug = 'cash'

          if (defaultMethod) {
            defaultSlug = defaultMethod.slug
          } else {
            // If no default is set, use the first active payment method or 'cash'
            const firstActiveMethod = this.paymentMethods.find(method => method.is_active === true)
            defaultSlug = firstActiveMethod ? firstActiveMethod.slug : 'cash'
          }

          this.paymentMethod = defaultSlug
          await this.$nextTick()
        }
      } catch (error) {
        // On error, keep the standard payment options available
        this.paymentMethods = getDefaultPaymentMethods()
        this.loadingPaymentMethods = false
        if (forceDefault || !this.paymentMethod || this.paymentMethod === '') {
          await this.$nextTick()
          this.paymentMethod = 'cash'
        }
      }
    },
    formatMoney(number) { return helper.formatMoney(number);},
    handleTransactionUpdated(data) {
      // Update local data with the new values from modal
      if (data.paymentMethod !== undefined) {
        this.paymentMethod = data.paymentMethod;
      }
      if (data.notes !== undefined) {
        this.notes = data.notes;
      }
      if (data.client !== undefined) {
        this.client = data.client;
      }

      // Update the checkout details to reflect changes in the modal
      if (this.checkoutDetails) {
        this.checkoutDetails.client = data.client || { name: '-' };
      }
    },
    handleRestoreTransaction(data) {
      // Restore the transaction for modification
      this.checkoutResultModalState = false;

      // Restore cart items if available
      if (data.items && data.items.length > 0) {
        // Clear current cart
        this.cart = [];

        // Add items back to cart
        // Note: This assumes items have the necessary structure
        // You may need to fetch full product details or map the structure
        window.vueOpener.open({
          message: this.$t('transaction_restored') || 'Transaction restored. Note: Full product modification may require reloading items.',
          type: 'info',
          position: 'bottom',
        });

        // Optionally: You could add logic here to restore items to cart
        // For example, fetch the products and add them back
      }
    },
    // Toggle kiosk mode: hide layout and toggle fullscreen
    async toggleKioskMode() {
      try {
        if (!this.kioskMode) {
          // Enter kiosk mode
          this.prevNavFullSize = this.$store.getters.NAV_FULL_SIZE;

          // Use Vuex to hide navbar/sidebar
          this.$store.commit('SET_KIOSK_MODE', true);

          // Expand content area
          this.$store.commit('SET_NAV_FULL_SIZE', false);
          // Enter fullscreen if supported
          if (!document.fullscreenElement && document.documentElement.requestFullscreen) {
            await document.documentElement.requestFullscreen().catch(() => {});
          }
          this.kioskMode = true;
        } else {
          // Exit kiosk mode

          // Use Vuex to show navbar/sidebar
          this.$store.commit('SET_KIOSK_MODE', false);

          // Restore content area width
          if (this.prevNavFullSize !== null) {
            this.$store.commit('SET_NAV_FULL_SIZE', this.prevNavFullSize);
          }
          // Exit fullscreen if we're in it
          if (document.fullscreenElement && document.exitFullscreen) {
            await document.exitFullscreen().catch(() => {});
          }
          this.kioskMode = false;
        }
      } catch (e) {
        // No-op on errors; aim for graceful degradation
      }
    },
    // Ensure layout is restored if user exits fullscreen via ESC
    handleFullscreenChange() {
      if (!document.fullscreenElement && this.kioskMode) {
        // Simulate turning kiosk mode off without trying to exit fullscreen again
        this.$store.commit('SET_KIOSK_MODE', false);

        if (this.prevNavFullSize !== null) {
          this.$store.commit('SET_NAV_FULL_SIZE', this.prevNavFullSize);
        }
        this.kioskMode = false;
      }
    },
    toggleControls(index) { this.selectedItemIndex = this.selectedItemIndex === index ? null : index; },
    addToCart(product) {
      if (product.has_variants && product.variants && product.variants.length > 0) {
          this.selectedProductForVariants = product;
          this.showVariantModal = true;
          return;
      }
      const cartItem = this.cart.find(item => item.id === product.id && !item.variant_id);
      if (cartItem) {
        cartItem.quantity++;
        cartItem.totalPrice = cartItem.quantity * cartItem.inventories[0].price;
      } else {
        this.cart.push({ ...product, quantity: 1, totalPrice: product.inventories[0].price });
      }

    },
    addVariantToCart(variant) {
        const product = this.selectedProductForVariants;
        const cartItem = this.cart.find(item => item.id === product.id && item.variant_id === variant.id);

        if (cartItem) {
            cartItem.quantity++;
            cartItem.totalPrice = cartItem.quantity * cartItem.inventories[0].price;
        } else {
             this.cart.push({
                ...product,
                id: product.id,
                variant_id: variant.id,
                label: `${product.label} - ${variant.name}`,
                image: variant.image || product.image,
                quantity: 1,
                inventories: [{
                    id: variant.inventory.id,
                    price: variant.price,
                    purchase_price: variant.inventory.purchase_price,
                    quantity: variant.inventory.quantity
                }],
                totalPrice: variant.price
            });
        }
        this.showVariantModal = false;
        this.selectedProductForVariants = null;
    },
    removeFromCart(index) {
      this.cart.splice(index, 1);
      this.selectedItemIndex = null;

      // If cart is empty, close the modal
      if (this.cart.length === 0) {
        this.showReceiptModal = false;
      }
    },
    increaseQuantity(item) {
      item.quantity++;
      item.totalPrice = item.quantity * item.inventories[0].price;
    },
    decreaseQuantity(item) {
      if (item.quantity > 1) {
        item.quantity--;
        item.totalPrice = item.quantity * item.inventories[0].price;
      } else {
        this.removeFromCart(this.cart.indexOf(item));
      }
    },
    openChargeModal() {
      if (!this.cart.length) {
        alert("Please ensure there are products in the cart.");
        return;
      }
      this.showReceiptModal = false;
      this.currentReceiptHtml = null;
      this.chargeModalState = true;
    },
    handleChargeModalClose() {
      if (this.currentReceiptHtml) {
        this.cancelCart();
      } else {
        this.chargeModalState = false;
      }
    },
    handleChargeConfirm(data) {
      this.paymentMethod = data.paymentMethod;
      this.notes = data.notes;
      this.checkout();
    },
    async checkout() {
      this.loading = true;
      if (!this.cart.length) {
        alert("Please ensure there are products in the cart and a cashier is selected.");
        return;
      }
      try{
      this.previousCartStatus = "paid";

      const payload = {
        payment_status: "paid",
        payment_method: this.paymentMethod,
        sale_date: new Date().toISOString(),
        cash_register_id: this.currentRegister?.id,
        cashier_id : this.currentRegister?.cashier_id,
        notes : this.notes,
        discount : this.discount,
        client_id : this.client?.id,
        products: this.cart.map((item) => {
          return {
            id: item.id,
            name: item.label,
            quantity: Number(item.quantity) || 0,
            price: Number(item.inventories[0].price) || 0,
            price_includes_tax: (item.price_includes_tax === true) || (item.product?.price_includes_tax === true),
            purchase_price: item.inventories[0].purchase_price,
            inventory_id : item.inventories[0].id
          };
        }),
      };

      let response;

      // Check if we're updating an existing transaction
      if (this.editingTransactionId) {
        response = await helper.customPatch(`${process.env.VUE_APP_API}/pos/${this.editingTransactionId}`, payload, {showToast:false});
        // Clear the editing ID after successful update
        this.editingTransactionId = null;
      } else {
        // Create new transaction
        response = await helper.customPost(`${process.env.VUE_APP_API}/pos`, payload, {showToast:false});
        // Store transaction ID for receipt
        if (response && response.data && response.data.reference) {
          this.lastTransactionId = `${String(response.data.reference)}`
        }
      }

      this.loading = false;
      this.showReceiptModal = false;
      this.submittedCart = this.cart;

      // Prepare checkout summary details and generate HTML
      const data = this.buildReceiptData();
      this.checkoutDetails = {
        id: response?.data?.id,
        reference: response?.data?.reference || this.lastTransactionId,
        date: data.date,
        cashier: data.cashier,
        client: data.client,
        subtotal: data.subtotal,
        discount: data.discount,
        tax: data.tax,
        total: data.total,
        items: data.items,
        business: data.business,
      };

      const template = (await this.loadReceiptTemplate()) || this.getDefaultReceiptTemplate();
      this.currentReceiptHtml = this.renderTemplate(template, data);

      }catch(error){
        window.vueOpener.open({
          message: 'something went wrong',
          type: 'error',
          position: 'bottom',
        });
        this.loading = false;
      }

    },
    async onHold() {
      this.previousCartStatus = null;
      this.loading = true;
      if (!this.cart.length) {
        alert("Please ensure there are products in the cart and a cashier is selected.");
        return;
      }

      const payload = {
        payment_status: "pending",
        payment_method: this.paymentMethod,
        sale_date: new Date().toISOString(),
        cash_register_id: this.currentRegister?.id,
        cashier_id : this.currentRegister?.cashier_id,
        notes : this.notes,
        discount : this.discount,
        client_id : this.client?.id,
        products: this.cart.map((item) => {
          return {
            id: item.id,
            name: item.label,
            quantity: Number(item.quantity) || 0,
            price: Number(item.inventories[0].price) || 0,
            purchase_price: item.inventories[0].purchase_price,
            inventory_id : item.inventories[0].id
          };
        }),
      };

      let response;

      // Check if we're updating an existing transaction
      if (this.lastTransactionId && this.lastTransactionId.startsWith('TXN-')) {
        const transactionId = this.lastTransactionId.replace('TXN-', '');
        response = await helper.customPatch(`${process.env.VUE_APP_API}/pos/${transactionId}`, payload, {showToast:true});
        window.vueOpener.open({
          message: 'Transaction updated successfully',
          type: 'success',
          position: 'bottom',
        });
      } else {
        // Create new transaction
        response = await helper.customPost(`${process.env.VUE_APP_API}/pos`, payload, {showToast:true});
        // Store transaction ID for receipt
        if (response && response.data && response.data.id) {
          this.lastTransactionId = `TXN-${String(response.data.id).padStart(6, '0')}`
        }
      }

      this.loading = false;
      this.showReceiptModal = false;
      this.submittedCart = this.cart;
      // this.cancelCart();
    },

    openNewSale() {
      this.previousCartStatus = null;
      this.cart = [];
    },
    async printReceipt() {
      // Build receipt HTML from template and data
      const template = (await this.loadReceiptTemplate()) || this.getDefaultReceiptTemplate();
      const data = this.buildReceiptData();
      const renderedContent = this.renderTemplate(template, data);
      // Preferred: if running inside Electron, send to main for silent/background printing
      try {
        // contextBridge API via preload (recommended)
        if (window?.electron?.printReceipt) {
          const res = await window.electron.printReceipt({ html: renderedContent });
          // If main returns an object with ok=false, fall through to fallback
          if (!res || res.ok) return;
        }
        // Alternative naming fallback
        if (window?.electronAPI?.printReceipt) {
          await window.electronAPI.printReceipt({ html: renderedContent });
          return;
        }
        // Legacy direct ipcRenderer exposure (if injected)
        if (window?.ipcRenderer?.send) {
          window.ipcRenderer.send('print-receipt', { html: renderedContent });
          return;
        }
      } catch (e) {
        // If Electron call fails, continue to browser fallback
      }

      // Browser fallback: print via hidden iframe (opens dialog in browsers)
      // Fallback: print via hidden iframe (robust)
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      iframe.style.visibility = 'hidden';
      document.body.appendChild(iframe);
      const doc = iframe.contentWindow.document;
      doc.open();
      const html = [
        '<html>',
        '<head><style>@media print { @page { margin: 0; size: auto; } body { margin: 0; } }</style></head>',
        '<body>' + renderedContent + '</body>',
        '</html>'
      ].join('');
      doc.write(html);
      doc.close();
      // Give the iframe a tick to render, then print
      setTimeout(() => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        } finally {
          setTimeout(() => {
            if (iframe && iframe.parentNode) {
              iframe.parentNode.removeChild(iframe);
            }
          }, 1000);
        }
      }, 0);
    },
    async loadReceiptTemplate() {
      try {
        const resp = await helper.customGet(`${process.env.VUE_APP_API}/templates`)
        if (resp?.data?.data?.length) {
          const existing = resp.data.data.find(t => (t.type === 'pos_receipt'))
          if (existing && existing.data) {
            let templateData = existing.data
            // Parse if it's a JSON string
            if (typeof templateData === 'string') {
              try {
                templateData = JSON.parse(templateData)
              } catch (e) {
                // If not JSON, treat as plain template text (legacy format)
                return templateData
              }
            }
            // Return the template portion if it's the new format
            if (templateData.template) {
              return templateData.template
            }
            // Otherwise return as-is
            return typeof templateData === 'string' ? templateData : JSON.stringify(templateData)
          }
        }
        return null
      } catch (e) {
        alert('Failed to load receipt template:', e)
        return null
      }
    },

    buildReceiptData() {
      const now = new Date()
      // Generate receipt ID if not available (for preview/draft receipts)
      const receiptId = this.lastTransactionId
      // Generate POS ID
      const posId = this.lastTransactionId
      // Get cashier name
      const cashier = this.users.find(u => u.id === this.currentRegister?.cashier_id) || this.user

      // Calculate discount amount
      const discountAmount = this.subTotal * (this.discount / 100)
      const hasDiscount = this.discount > 0

      return {
        business: {
          name: this.business?.name || 'My Business',
          address: this.business?.address || '123 Main St',
          phone: this.business?.phone || '+212 600 000 000',
          email: this.business?.email || 'contact@business.com',
          taxId: this.business?.tax_id || this.business?.ice || 'ICE123456789',
          logo: this.business?.logo || '',
        },
        receipt_id: receiptId,
        pos_id: posId,
        cashier,
        date: now.toLocaleString(),
        client: this.client || { name: 'Walk-in Client' },
        subtotal: this.formatMoney(this.subTotal),
        discount: this.discount > 0 ? `${this.discount}%` : '0%',
        has_discount: hasDiscount,
        discount_amount: this.formatMoney(discountAmount),
        tax: this.formatMoney(this.totalTax),
        tax_rate: this.business?.vat ? `${this.business.vat}%` : '20%',
        tax_label: this.$t('tax'),
        total: this.formatMoney(this.cartTotal),
        paid_amount: this.formatMoney(this.cartTotal), // Assuming paid in full
        change_amount: this.formatMoney(0), // Placeholder
        payment_method: this.paymentMethod,
        items: this.submittedCart.length > 0 ? this.submittedCart.map(item => ({
          name: item.label,
          qty: item.quantity,
          price: this.formatMoney(item.inventories[0].price),
          line_total: this.formatMoney(item.totalPrice)
        })) : this.cart.map(item => ({
          name: item.label,
          qty: item.quantity,
          price: this.formatMoney(item.inventories[0].price),
          line_total: this.formatMoney(item.totalPrice)
        })),
        //translation
        cashier_label : this.$t('cashier'),
        subtotal_label: this.$t('subtotal'),
        discount_label: this.$t('discount'),
        product_label: this.$t('product'),
        product_quantity: this.$t('quantity'),
        price_label: this.$t('price'),
        total_label: this.$t('total'),
      }
    },
    // Template renderer that supports {{variable}} and {{#items}}...{{/items}}
    renderTemplate(tpl, data) {
      if (!tpl) return ''
      let output = String(tpl)

      // Process items loop - matches {{#items}}...{{/items}}
      const loopRegex = /\{\{#items\}\}([\s\S]*?)\{\{\/items\}\}/g
      output = output.replace(loopRegex, (_match, inner) => {
        if (!Array.isArray(data.items)) return ''
        return data.items.map(item => this.replaceTokens(inner, Object.assign({}, data, item))).join('')
      })

      // Process discount conditional - matches {{#if_discount}}...{{/if_discount}}
      const discountRegex = /\{\{#if_discount\}\}([\s\S]*?)\{\{\/if_discount\}\}/g
      output = output.replace(discountRegex, (_match, inner) => {
        // Check both has_discount flag and discount value
        if (data.has_discount !== undefined) {
          return data.has_discount ? inner : ''
        }
        // Fallback: parse discount value
        const discountValue = parseFloat(String(data.discount || '0').replace(/[^0-9.-]/g, ''))
        return discountValue > 0 ? inner : ''
      })

      // Inject special components
      output = this.injectSpecialComponents(output, data)

      // Replace simple tokens
      output = this.replaceTokens(output, data)

      return output
    },

    injectSpecialComponents(html, data) {
      // Inject Logo
      if (data.business && data.business.logo) {
        html = html.replace(/\{\{LOGO\}\}/g, `<img src="${data.business.logo}" style="max-width:120px; max-height:80px; display:block; margin:0 auto 8px;">`)
      } else {
        html = html.replace(/\{\{LOGO\}\}/g, '')
      }

      // Inject Barcode
      const barcodeDataUrl = this.generateBarcode(data.pos_id || data.receipt_id)
      html = html.replace(/\{\{BARCODE\}\}/g, `<img src="${barcodeDataUrl}" style="max-width:100%; height:50px; display:block; margin:8px auto;">`)

      // Inject QR Code
      const qrData = `Receipt:${data.pos_id}\nTotal:${data.total}\nDate:${data.date}`
      const qrDataUrl = this.generateQRCode(qrData)
      html = html.replace(/\{\{QRCODE\}\}/g, `<img src="${qrDataUrl}" style="width:100px; height:100px; display:block; margin:8px auto;">`)

      return html
    },

    generateBarcode(text) {
      // Simple barcode generation using canvas
      try {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        canvas.width = 200
        canvas.height = 50

        // Simple Code 128-like pattern (simplified for demo)
        ctx.fillStyle = 'white'
        ctx.fillRect(0, 0, canvas.width, canvas.height)
        ctx.fillStyle = 'black'

        // Draw bars
        let x = 10
        const barWidth = 2
        for (let i = 0; i < text.length; i++) {
          const code = text.charCodeAt(i)
          const pattern = code % 2 === 0 ? [1, 0, 1, 0, 1] : [1, 1, 0, 0, 1]
          pattern.forEach(bar => {
            if (bar === 1) {
              ctx.fillRect(x, 5, barWidth, 30)
            }
            x += barWidth
          })
        }

        // Draw text below barcode
        ctx.font = '10px monospace'
        ctx.textAlign = 'center'
        ctx.fillText(text, canvas.width / 2, 45)

        return canvas.toDataURL()
      } catch (e) {
        return ''
      }
    },

    generateQRCode(text) {
      // Simple QR code generation (basic pattern for demo)
      try {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        const size = 100
        const moduleSize = 5
        canvas.width = size
        canvas.height = size

        ctx.fillStyle = 'white'
        ctx.fillRect(0, 0, size, size)
        ctx.fillStyle = 'black'

        // Create simple QR-like pattern
        for (let i = 0; i < size; i += moduleSize) {
          for (let j = 0; j < size; j += moduleSize) {
            // Create pseudo-random pattern based on text
            const hash = (text.charCodeAt((i + j) % text.length) + i + j) % 2
            if (hash === 0) {
              ctx.fillRect(i, j, moduleSize, moduleSize)
            }
          }
        }

        return canvas.toDataURL()
      } catch (e) {
        return ''
      }
    },

    replaceTokens(str, data) {
      return str.replace(/\{\{\s*([a-zA-Z0-9_.]+)\s*\}\}/g, (m, key) => {
        const parts = key.split('.')
        let val = data
        for (const p of parts) {
          if (val && Object.prototype.hasOwnProperty.call(val, p)) {
            val = val[p]
          } else {
            return m
          }
        }
        return val || ''
      })
    },

    getDefaultReceiptTemplate() {
      return `
<div style="width:280px; margin:0 auto; font-family: Arial, sans-serif; color:#000">
  <div style="text-align:center; margin-bottom:8px">
    <div style="font-weight:bold">{{business.name}}</div>
    <div style="font-size:12px">{{business.address}}</div>
    <div style="font-size:12px">{{business.phone}}</div>
    <div style="font-size:12px; margin-top:4px">{{date}}</div>
    <div style="font-size:12px; margin-top:4px">Id:{{pos_id}}</div>
  </div>
  <hr/>
  <div style="font-size:11px; margin-bottom:8px">
    <div style="display:flex; justify-content:space-between"><span>Receipt ID:</span><span>{{receipt_id}}</span></div>
    <div style="display:flex; justify-content:space-between"><span>POS ID:</span><span>{{pos_id}}</span></div>
    <div style="display:flex; justify-content:space-between"><span>{{cashier_label}}:</span><span>{{cashier.name}}</span></div>
  </div>
  <hr/>
  <table style="width:100%; font-size:12px">
    <thead>
      <tr>
        <th style="text-align:left">{{product_label}}</th>
        <th style="text-align:center">{{product_quantity}}</th>
        <th style="text-align:right">{{price_label}}</th>
        <th style="text-align:right">{{total_label}}</th>
      </tr>
    </thead>
    <tbody>
      {{#items}}
      <tr>
        <td style="text-align:left">{{name}}</td>
        <td style="text-align:center">{{qty}}</td>
        <td style="text-align:right">{{price}}</td>
        <td style="text-align:right">{{line_total}}</td>
      </tr>
      {{/items}}
    </tbody>
  </table>
  <hr/>
  <div style="font-size:12px">
    <div style="display:flex; justify-content:space-between"><span>{{subtotal_label}}:</span><span>{{subtotal}}</span></div>
    <div style="display:flex; justify-content:space-between"><span>{{discount_label}}:</span><span>{{discount}}</span></div>
    <div style="display:flex; justify-content:space-between"><span>{{tax_label}}:</span><span>{{tax}}</span></div>
    <div style="display:flex; justify-content:space-between; font-weight:bold"><span>{{total_label}}:</span><span>{{total}}</span></div>
  </div>
  <div style="text-align:center; margin-top:8px; font-size:12px"></div>
</div>`
    },
    async getProducts() {
      if (this.loading) return;

      try {
        this.fetched_all_products = false
        this.loading = true;
        const url = this.selected_categories.length ? `${process.env.VUE_APP_API}/products?page=${this.page}&per_page=${this.per_page}&categories=${this.selected_categories.join(',')}` : `${process.env.VUE_APP_API}/products?page=${this.page}&per_page=${this.per_page}`;
        const response = await helper.customGet(url);
        const products = response.data.data;
        this.products = [...new Set([...this.products, ...products])];
        this.page++;
        this.loading = false;
        if(products.length == 0){
          this.fetched_all_products = true
        }else{
          this.fetched_all_products = false
        }
      } catch (error) {
        this.loading = false;
      }
    },
    async getCategories(){
      try{
        let url = `${process.env.VUE_APP_API}/categories?page=1&per_page=100`
        this.categories = []
        const response = await helper.customGet(url)
        this.categories = response.data.data
      }catch(error){
        this.loading = false
      }
    },
    modalClosed(){
        this.isModalOpen = false
        if(document.getElementById('close-modal-btn')){
            document.getElementById('close-modal-btn').click()
        }
        this.$emit('modalClosed', true)
    },
    handleScroll() {
      const posProducts = document.getElementById('pos-products');

      if (this.fetched_all_products === true) {
        // remove scroll listener
        posProducts.removeEventListener('scroll', this.handleScroll);
        return; // stop execution
      }

      if (posProducts.scrollTop + posProducts.clientHeight >= posProducts.scrollHeight - 200 && this.loading == false && this.fetched_all_products === false) {
        this.getProducts();
      }
    },
    cancelCart() {
      this.cart = [];
      this.submittedCart = [];
      this.lastTransactionId = null; // Clear transaction ID for new transaction
      this.isModalOpen = false;
      this.showReceiptModal = false; // Also close the mobile receipt modal
      this.checkoutResultModalState = false; // Close checkout summary modal if open
      this.chargeModalState = false; // Close charge modal
      this.currentReceiptHtml = null; // Clear receipt
      this.discount = 0;
      this.notes = '';
      this.client = null;
      if(document.getElementById('close-modal-btn')){
        document.getElementById('close-modal-btn').click();
      }
      this.$emit('modalClosed', true);
    },
    openCreateClientModal(){
        this.createClientState = !this.createClientState;
    },
    saveClient(data){
        if(data.client){
          this.client = data.client;
        }
      if(data && data.state != undefined){
            this.createClientState = data.state;
        }
    },
    formatDateTime(date) {
      if (!date) return '';
      const dt = new Date(date);
      return dt.toLocaleString();
    },
    showRegisterModal(){
      this.isModalOpen = true;
    },
    async fetchCurrentRegister() {
      try {
        this.loading = true;
        const response = await helper.customGet(`${process.env.VUE_APP_API}/cash-register`);
        if (response.data && response.data.id) {
          this.currentRegister = response.data;
          this.registerStatus = response.data.status;
        } else {
          this.resetRegister();
        }

        this.loading = false;
      } catch (error) {
        this.resetRegister();
        this.loading = false;
      }
    },
    resetRegister() {
      this.currentRegister = {
        opening_balance: 0,
        closing_balance: 0,
        status: 'closed',
        opened_at: null,
        closed_at: null,
        cashier_id: this.currentRegister.cashier_id,
        notes: ''
      };
      this.totalSales = 0;
      this.transactionCount = 0;
    },
    updatePrice(item) {
      item.totalPrice = item.quantity * item.inventories[0].price;
    },
    openNotesModal() {
      this.notesModalState = true;
      if (window.innerWidth < 992) {
        this.showReceiptModal = false; // Hide receipt modal before showing notes modal
      }
    },
    saveNotes(data) {
      if(data.notes != undefined){
        this.notes = data.notes;
      }
      if(data && data.state != undefined){
        this.notesModalState = data.state;
      }
      if (window.innerWidth < 992 && this.cart.length > 0) {
        this.showReceiptModal = true; // Show receipt modal again after saving notes
      }
    },
    openDiscountModal() {
      this.discountModalState = true;
      if (window.innerWidth < 992) {
        this.showReceiptModal = false; // Hide receipt modal before showing discount modal
      }
    },
    saveDiscount(data) {
      if(data.discount != undefined){
        this.discount = data.discount;
      }
      if(data && data.state != undefined){
        this.discountModalState = data.state;
      }
      if (window.innerWidth < 992 && this.cart.length > 0) {
        this.showReceiptModal = true; // Show receipt modal again after saving discount
      }
    },
    openTransactionsModal() {
      this.transactionsModalState = true;
      if (window.innerWidth < 992) {
        this.showReceiptModal = false; // Hide receipt modal before showing transactions modal
      }
    },
    async loadTransactionToPos(transaction) {
      try {
        // Clear current cart
        this.cart = [];
        this.submittedCart = [];

        // Set the transaction ID being edited
        this.editingTransactionId = transaction.id;

        // Load transaction details to get products
        const response = await helper.customGet(`${process.env.VUE_APP_API}/pos/${transaction.id}`);
        const transactionData = response.data;

        // Set transaction ID for receipt
        this.lastTransactionId = `TXN-${String(transaction.id).padStart(6, '0')}`;

        // Load products into cart
        if (transactionData.products && transactionData.products.length > 0) {
          for (const productItem of transactionData.products) {
            // Find the product in our products list or fetch it
            let product = this.products.find(p => p.id === productItem.product_id);

            if (!product) {
              // If product not in current list, fetch it
              try {
                const productResponse = await helper.customGet(`${process.env.VUE_APP_API}/products/${productItem.product_id}`);
                product = productResponse.data;
                // Add to products list
                this.products.push(product);
              } catch (error) {
                alert('Error fetching product:', error);
                continue;
              }
            }

            if (product) {
              // Create cart item with transaction data
              const cartItem = {
                ...product,
                quantity: productItem.quantity,
                totalPrice: productItem.quantity * productItem.unit_price,
                inventories: [{
                  ...product.inventories[0],
                  price: productItem.unit_price // Use the price from transaction
                }]
              };

              this.cart.push(cartItem);
            }
          }
        }

        // Set other transaction data
        this.notes = transactionData.notes || '';
        this.discount = transactionData.discount || 0;
        this.client = transactionData.client || null;

        // Set cashier
        if (transactionData.cashier_id) {
          this.currentRegister.cashier_id = transactionData.cashier_id;
        }

        // Show success message
        // window.vueOpener.open({
        //   message: `Transaction ${transaction.reference} loaded successfully`,
        //   type: 'success',
        //   position: 'bottom',
        // });

        // Show receipt modal on mobile
        if (window.innerWidth < 992 && this.cart.length > 0) {
          this.showReceiptModal = true;
        }

      } catch (error) {
        alert('Error loading transaction to POS:', error);
        window.vueOpener.open({
          message: 'Error loading transaction',
          type: 'error',
          position: 'bottom',
        });
      }
    },
    toggleCategory(categoryId) {
      const index = this.selected_categories.indexOf(categoryId);
      if (index > -1) {
        this.selected_categories.splice(index, 1);
      } else {
        this.selected_categories.push(categoryId);
      }
      this.page = 1;
      this.products = [];
      this.getProducts();
    },
    async getUsers() {
        try {
            this.loading = true;
            const response = await helper.customGet(`${process.env.VUE_APP_API}/users`);
            this.users = response.data.data;
            this.users = this.users.filter(user => user.id !== this.user.id)
            this.currentRegister.cashier_id = this.user.id; // Set current user as default cashier
        } catch (e) {
            this.$swal.fire({
                icon: 'error',
                title: this.$t('error'),
                text: e.message
            });
        } finally {
            this.loading = false;
        }
    },
    async loadPrinterIntegration() {
      try {
        const response = await helper.customGet(`${process.env.VUE_APP_API}/integrations?type=printer`);
        if (response?.data?.data?.length) {
          const integration = response.data.data[0];
          this.printerIntegration = integration;
        } else {
          this.printerIntegration = null;
        }
      } catch (e) { /* ignore */ }
    },
    handleResize() {
      // Check if we need to show/hide the receipt modal based on screen size
      if (window.innerWidth >= 992) {
        this.showReceiptModal = false; // Hide modal on desktop
      }
    },
    toggleBarcodeInput() {
      this.showBarcodeInput = !this.showBarcodeInput;
      if (this.showBarcodeInput) {
        this.$nextTick(() => {
          this.$refs.barcodeInput?.focus();
        });
      } else {
        this.barcodeInput = '';
      }
    },
    handleBarcodeBlur() {
      // Only hide if the input is empty
      if (!this.barcodeInput) {
        this.showBarcodeInput = false;
      }
    },
    async processBarcode() {
      if (!this.barcodeInput) return;

      try {
        this.loading = true;
        const response = await helper.customGet(`${process.env.VUE_APP_API}/products?barcode=${this.barcodeInput}`);
        const products = response.data.data;

        if (products && products.length > 0) {
          // Add the first matching product to cart
          this.addToCart(products[0]);
          window.vueOpener.open({
              message: this.$t('product_added'),
              type: 'success',
              position : 'bottom',
            });
          // Clear the input and keep it focused for next scan
          this.barcodeInput = '';
          this.$refs.barcodeInput?.focus();
        } else {
           window.vueOpener.open({
              message: this.$t('barcode_not_found'),
              type: 'error',
              position : 'bottom',
            });
          // Clear the input and keep it focused for next scan
          this.barcodeInput = '';
          this.$refs.barcodeInput?.focus();
        }
      } catch (error) {
        window.vueOpener.open({
              message: this.$t('something_went_wrong'),
              type: 'error',
              position : 'bottom',
            });
      } finally {
        this.loading = false;
      }
    },
  },
  async created() {
    this.getCategories();
    this.getProducts();
    this.getUsers()
    this.fetchCurrentRegister();
    // Fetch payment methods and set default
    await this.fetchPaymentMethods(true);
  },
  mounted(){
    window.mixpanel.track("pos");
    //check permissions
    if(helper.checkAccess('view_pos') == -1) return this.$router.push({path : '/'});
    // offline: do not force subscribe
    if(helper.checkAccess('view_pos') == -2) return this.$router.push({path : '/subscribe'})

    document.getElementById('pos-products').addEventListener('scroll', this.handleScroll);
    window.addEventListener('resize', this.handleResize);
    // Listen for ESC or user-initiated fullscreen exit
    document.addEventListener('fullscreenchange', this.handleFullscreenChange);
  },
  beforeDestroy() {
    if(document.getElementById('pos-products')){
      document.getElementById('pos-products').removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('resize', this.handleResize);
    }
    document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
    // Ensure layout is restored when leaving POS
    if (this.kioskMode) {
      try {
        const sidebarEl = document.querySelector('.navbar-menu');
        const topNavEl = document.querySelector('nav.navbar.navbar-expand-lg.navbar-light');
        if (sidebarEl) {
          if (sidebarEl.classList.contains('d-none')) sidebarEl.classList.remove('d-none');
          if (sidebarEl.classList.contains('d-lg-none')) sidebarEl.classList.remove('d-lg-none');
        }
        if (topNavEl && topNavEl.classList.contains('d-none')) topNavEl.classList.remove('d-none');
        if (this.prevNavFullSize !== null) {
          this.$store.commit('SET_NAV_FULL_SIZE', this.prevNavFullSize);
        }
        if (document.fullscreenElement && document.exitFullscreen) {
          document.exitFullscreen().catch(() => {});
        }
      } finally {
        this.kioskMode = false;
      }
    }
  },
  watch : {
    isModalOpen(){
      if(this.isModalOpen === false){
        this.orderConfirmed = false;
        this.fetchCurrentRegister();
      }
    },
    async search(){
        if(this.search.length > 2){
            this.loading = true;
            this.products = [];
            clearTimeout(this.timer_id);
            this.timer_id = setTimeout(async () => {
                const response = await helper.customGet(`${process.env.VUE_APP_API}/products?keyword=${this.search}&per_page=${this.per_page}`);
                this.count = response.data.count;
                this.products = response.data.data;
                this.loading = false;
            }, 500);
        }
        if(this.search == '') {
          clearTimeout(this.timer_id);
          this.timer_id = null;
          this.page = 1;
          this.products = [];
          return this.getProducts();
        }
    }
  }
};
</script>

<style scoped>
.container {
  background-color: #f9f9f9;
  min-height: 100vh;
}
.card {
  cursor: pointer;
}
.card-img-top {
  height: 80px;
  object-fit: cover;
  border-radius: 4px;
}

@media (min-width: 768px) {
  .card-img-top {
    height: 100px;
  }
}

@media (min-width: 992px) {
  .card-img-top {
    height: 120px;
  }
}
.card-img-wrapper {
  position: relative;
}

.product-initials {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 1.75rem;
  font-weight: 750;
  letter-spacing: 0.04em;
  line-height: 1;
  text-shadow: 0 1px 2px rgba(15, 23, 42, 0.2);
  user-select: none;
}

/* Fixed Receipt Section */
.fixed-receipt {
  position: fixed;
  top: 20px;
  right: 20px;
  width: 350px;
  z-index: 1000;
  max-height: calc(100vh - 40px);
  display: flex;
  flex-direction: column;
}

/* Scrollable Cart Items */
.scrollable-cart {
  flex: 1;
  overflow-y: auto;
  padding-right: 10px;
}

/* Fixed Bottom Section */
.fixed-bottom-section {
  background-color: white;
  padding: 16px;
  border-top: 1px solid #e9ecef;
}

/* Mobile Cart Button Styles */
.fixed-bottom {
  z-index: 1040;
  background-color: transparent;
}

/* Modal styling */
.modal {
  background-color: rgba(0, 0, 0, 0.5);
}
.modal-backdrop {
  z-index: 1050;
}
.modal.show {
  z-index: 1051;
}
.modal-dialog {
  margin: 0;
  max-width: 100%;
  height: 100%;
}
.modal-content {
  border-radius: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
}
.modal-body {
  flex: 1;
  overflow-y: auto;
}

/* Make products display better on mobile */
@media (max-width: 767px) {
  .row-cols-2 {
    margin-bottom: 70px; /* Add space for the fixed cart button */
  }
  .card-body {
    padding: 3px !important;
  }
  .card-title {
    font-size: 10px !important;
  }
  .card-text {
    font-size: 9px !important;
  }
  .card-img-top {
    height: 70px;
  }
  .product-initials {
    font-size: 1.35rem;
  }
}

/* Tablet optimizations */
@media (min-width: 768px) and (max-width: 991px) {
  .card-body {
    padding: 4px !important;
  }
  .card-title {
    font-size: 11px !important;
  }
  .card-text {
    font-size: 10px !important;
  }
  /* Smaller action buttons on tablets */
  .btn-sm {
    padding: 0.25rem 0.4rem;
    font-size: 0.75rem;
  }
  /* Compact cart items */
  .list-group-item {
    padding: 0.4rem 0.5rem !important;
  }
  /* Smaller scrollable cart padding */
  .scrollable-cart {
    padding: 0.5rem !important;
  }
  .fixed-bottom-section {
    padding: 10px !important;
    font-size: 0.8rem !important;
  }
  .fixed-bottom-section .btn {
    font-size: 0.7rem !important;
    padding: 0.25rem 0.4rem !important;
  }
  .fixed-bottom-section .btn i {
    font-size: 14px !important;
  }
  .fixed-bottom-section .fw-bold {
    font-size: 0.8rem !important;
  }
}

.dashed-category{
  border : 3px dashed #303030 !important
}

.category-initial {
  align-items: center;
  color: #fff;
  display: flex;
  font-size: 1.35rem;
  font-weight: 700;
  height: 100%;
  justify-content: center;
  line-height: 1;
  text-transform: uppercase;
  width: 100%;
}

.pos-charge-button,
.fixed-bottom-section .pos-charge-button {
  min-height: 64px;
  padding: 0.9rem 1.25rem !important;
  border-radius: 0.75rem;
  font-size: 1.1rem !important;
  font-weight: 700;
  box-shadow: 0 0.25rem 0.6rem rgba(25, 135, 84, 0.25);
}

.pos-charge-icon {
  font-size: 1.5rem !important;
  vertical-align: middle;
}
</style>
